/**
 * The on-screen stacked-bar tooltip shows both numbers per segment —
 * "3,889 / 54%". PowerPoint's native data labels can only bind ONE number
 * (value or format thereof), and pptxgenjs exposes no per-point label text,
 * so the combined label is injected into the generated chart XML afterwards:
 * OOXML supports per-point `<c:dLbl>` overrides with free rich text.
 *
 * Entries are matched by chart title + series name + category name (never by
 * chart order), so the injection is independent of how many charts a deck has
 * and of pptxgenjs' emission order.
 */
export type StackedDualLabelEntry = {
  chartTitle: string;
  seriesName: string;
  categoryName: string;
  text: string;
  /** Delete this point's label instead — sub-threshold slivers can't fit one. */
  hidden?: boolean;
};

export type DualLabelStyle = {
  /** Hex colour without '#'. */
  colorHex: string;
  fontFace: string;
  fontSize: number;
};

const SECONDARY_DIMENSION_KEY = 'Secondary Dimension';
const COUNT_SUFFIX = ' Count';
const SHARE_SUFFIX = ' Share';

/**
 * Segments below this share get NO label at all: PowerPoint renders labels it
 * can't fit by piling them over their neighbours, and a run of small slices
 * turns into an unreadable smear. Dropping sliver labels loses nothing — they
 * were illegible — and gives the neighbouring labels room to overflow.
 *
 * 10%: the dual "1,025 / 14.2%" form needs roughly a tenth of the plot width
 * at the export font size — 5% still produced pile-ups in adjacent 5–9%
 * segments.
 */
const MIN_LABELED_SHARE = 0.1;

const formatCount = (value: number): string =>
  new Intl.NumberFormat('en').format(Math.round(value));

/** "0.54" → "54%", "0.497" → "49.7%" — 1dp, trailing zero trimmed. */
const formatShare = (share: number): string =>
  `${(share * 100).toFixed(1).replace(/\.0$/, '')}%`;

/**
 * Build the label entries from the export sheet, whose breakdown layout is
 * one row per segment with `<entity> Count` / `<entity> Share` column pairs
 * (see `breakdown-single-data-converter`). Chart orientation is the
 * transpose: series = segment, category = entity. Header rows carry strings
 * in the Count columns and are skipped by the numeric guard.
 */
export const buildStackedDualLabels = (
  chartTitle: string,
  sheetData: Record<string, string | number>[]
): StackedDualLabelEntry[] => {
  const entries: StackedDualLabelEntry[] = [];

  for (const row of sheetData) {
    const seriesName = row[SECONDARY_DIMENSION_KEY];
    if (typeof seriesName !== 'string' || !seriesName) continue;

    for (const key of Object.keys(row)) {
      if (!key.endsWith(COUNT_SUFFIX)) continue;
      const categoryName = key.slice(0, -COUNT_SUFFIX.length);
      const count = row[key];
      if (typeof count !== 'number') continue;

      const share = row[`${categoryName}${SHARE_SUFFIX}`];
      if (typeof share === 'number' && share < MIN_LABELED_SHARE) {
        entries.push({
          chartTitle,
          seriesName,
          categoryName,
          text: '',
          hidden: true,
        });
        continue;
      }

      const text =
        typeof share === 'number'
          ? `${formatCount(count)} / ${formatShare(share)}`
          : formatCount(count);

      entries.push({ chartTitle, seriesName, categoryName, text });
    }
  }

  return entries;
};

const unescapeXml = (value: string): string =>
  value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

const buildDLblXml = (
  idx: string,
  entry: StackedDualLabelEntry,
  style: DualLabelStyle
): string => {
  if (entry.hidden) {
    return `<c:dLbl><c:idx val="${idx}"/><c:delete val="1"/></c:dLbl>`;
  }
  const sz = Math.round(style.fontSize * 100);
  const runProps =
    `sz="${sz}"><a:solidFill><a:srgbClr val="${style.colorHex}"/></a:solidFill>` +
    `<a:latin typeface="${style.fontFace}"/>`;
  return (
    `<c:dLbl><c:idx val="${idx}"/>` +
    `<c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p>` +
    `<a:pPr><a:defRPr ${runProps}</a:defRPr></a:pPr>` +
    `<a:r><a:rPr lang="en-US" ${runProps}</a:rPr><a:t>${entry.text}</a:t></a:r>` +
    `</a:p></c:rich></c:tx>` +
    `<c:dLblPos val="ctr"/>` +
    `<c:showLegendKey val="0"/><c:showVal val="0"/><c:showCatName val="0"/>` +
    `<c:showSerName val="0"/><c:showPercent val="0"/><c:showBubbleSize val="0"/>` +
    `</c:dLbl>`
  );
};

/**
 * Pure chart-XML transform (exported for tests). Only touches stacked bar
 * charts whose title has label entries; unknown series/categories are left
 * untouched, so extra entries are harmless.
 */
const injectDualLabelsIntoChartXml = (
  xml: string,
  entries: StackedDualLabelEntry[],
  style: DualLabelStyle
): string => {
  if (!/<c:grouping val="(?:percent)?[sS]tacked"\/>/.test(xml)) return xml;

  // [\s\S] instead of the dotAll flag — tsconfig targets es2015, which
  // rejects /s (TS1501).
  const titleMatch = /<c:title>[\s\S]*?<a:t>(.*?)<\/a:t>/.exec(xml);
  if (!titleMatch?.[1]) return xml;
  const chartTitle = unescapeXml(titleMatch[1]);

  const chartEntries = entries.filter((e) => e.chartTitle === chartTitle);
  if (chartEntries.length === 0) return xml;

  return xml.replace(/<c:ser>[\s\S]*?<\/c:ser>/g, (serXml) => {
    const nameMatch = /<c:tx>[\s\S]*?<c:v>(.*?)<\/c:v>/.exec(serXml);
    if (!nameMatch?.[1]) return serXml;
    const seriesName = unescapeXml(nameMatch[1]);

    const catMatch = /<c:cat>[\s\S]*?<\/c:cat>/.exec(serXml);
    if (!catMatch) return serXml;
    const categoryByIdx = Array.from(
      catMatch[0].matchAll(/<c:pt idx="(\d+)"[^>]*><c:v>([\s\S]*?)<\/c:v>/g)
    ).map((match) => ({
      idx: match[1] ?? '',
      name: unescapeXml(match[2] ?? ''),
    }));

    const dLbls = categoryByIdx
      .map(({ idx, name }) => {
        const entry = chartEntries.find(
          (e) => e.seriesName === seriesName && e.categoryName === name
        );
        return entry ? buildDLblXml(idx, entry, style) : '';
      })
      .join('');
    if (!dLbls) return serXml;

    // Per-point overrides must be the first children of the series' <c:dLbls>
    // (pptxgenjs always emits one when showValue is on).
    return serXml.replace('<c:dLbls>', `<c:dLbls>${dLbls}`);
  });
};

const PPTX_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * Rewrite the chart parts of a finished deck with the per-segment dual
 * labels. Fail-safe: a deck without the fancy labels beats no deck, so any
 * error returns the original blob.
 */
export const injectStackedDualLabels = async (
  blob: Blob,
  entries: StackedDualLabelEntry[],
  style: DualLabelStyle
): Promise<Blob> => {
  if (entries.length === 0) return blob;
  try {
    // jszip is large and only needed once an export runs.
    const { default: JSZip } = await import('jszip');
    // ArrayBuffer keeps JSZip off its FileReader-based Blob path, which some
    // environments (workers, tests) lack.
    const zip = await JSZip.loadAsync(await blob.arrayBuffer());
    const chartFiles = Object.keys(zip.files).filter((name) =>
      /^ppt\/charts\/chart\d+\.xml$/.test(name)
    );
    for (const name of chartFiles) {
      const xml = await zip.files[name]?.async('string');
      if (!xml) continue;
      const updated = injectDualLabelsIntoChartXml(xml, entries, style);
      if (updated !== xml) zip.file(name, updated);
    }
    return await zip.generateAsync({
      type: 'blob',
      mimeType: PPTX_MIME,
      compression: 'DEFLATE',
    });
  } catch (error) {
    console.warn('Failed to inject stacked chart labels; keeping deck as-is', {
      error,
    });
    return blob;
  }
};
